import React, { useState, useMemo, useEffect, useRef } from "react";
import sideMenuData from "../../components/navBar/sideMenu";
import { useNavigate } from "react-router-dom";
import organizationData from "../../components/navBar/organizationData";
import { useSelector, useDispatch } from "react-redux";
import { setSideMenu } from "../../redux/reducers/user.reducer";
import useScreenConfig from "../workflow/screenConfig/useScreenConfig";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";

const ScreenSearch = () => {
  const [searchText, setSearchText] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const user = useSelector((state) => state.user.userData);
  const [sideMenuDetails, setSideMenuDetails] = useState([]);
  const isAdmin = useSelector((state) => state.user.isAdmin);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchRef = useRef(null);

  const { getSideMenu, sideMenuData: screenConfigData } = useScreenConfig({
    screenName: "Side Menu",
  });

  useEffect(() => {
    if (user?.department && user?.role) {
      getSideMenu({
        variables: {
          department: user?.department,
          role: user?.role,
          orgId: user?.orgId,
        },
      });
    }
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      setSideMenuDetails(organizationData);
      dispatch(setSideMenu(organizationData));
    } else {
      if (screenConfigData?.getSideMenu) {
        const parsedSideMenu = JSON.parse(
          screenConfigData?.getSideMenu?.screens
        );
        setSideMenuDetails(parsedSideMenu);
        dispatch(setSideMenu(parsedSideMenu));
      }
    }
  }, [screenConfigData, dispatch, isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const menuPathMapping = useMemo(() => {
    const mapping = new Map();

    const processMenuItems = (items, parentLabel = "") => {
      items.forEach((item) => {
        if (item.to) {
          mapping.set(item.label, {
            to: item.to,
            fullPath: parentLabel
              ? `${parentLabel} > ${item.label}`
              : item.label,
          });
        }

        if (item.subgroup) {
          item.subgroup.forEach((subItem) => {
            if (subItem.to) {
              mapping.set(subItem.label, {
                to: subItem.to,
                fullPath: `${item.label} > ${subItem.label}`,
              });
            }
          });
        }

        if (item.subgroup) {
          processMenuItems(item.subgroup, item.label);
        }
      });
    };
    if (isAdmin) {
      processMenuItems(organizationData);
    } else {
      processMenuItems(sideMenuData);
    }

    return mapping;
  }, [isAdmin]);

  const flattenedMenu = useMemo(() => {
    const flatten = [];

    if (isAdmin) {
      (Array.isArray(sideMenuDetails) ? sideMenuDetails : []).forEach(
        (item) => {
          if (item.to) {
            const screenInfo = menuPathMapping.get(item.label);
            if (screenInfo) {
              flatten.push({
                label: item.label,
                to: screenInfo.to,
                fullPath: screenInfo.fullPath,
                category: "Organization",
              });
            } else {
              flatten.push({
                label: item.label,
                to: item.to,
                fullPath: item.label,
                category: "Organization",
              });
            }
          }

          if (item.subgroup) {
            item.subgroup.forEach((subItem) => {
              if (subItem.to) {
                const screenInfo = menuPathMapping.get(subItem.label);
                if (screenInfo) {
                  flatten.push({
                    label: subItem.label,
                    to: screenInfo.to,
                    fullPath: screenInfo.fullPath,
                    category: item.label,
                  });
                } else {
                  flatten.push({
                    label: subItem.label,
                    to: subItem.to,
                    fullPath: `${item.label} > ${subItem.label}`,
                    category: item.label,
                  });
                }
              }
            });
          }
        }
      );
    } else {
      Object.entries(sideMenuDetails || {}).forEach(([category, screens]) => {
        const normalizedScreens = Array.isArray(screens)
          ? screens
          : typeof screens === "string"
          ? screens.split(",")
          : [];

        normalizedScreens.forEach((screenLabel) => {
          const screenInfo = menuPathMapping.get(screenLabel);
          if (screenInfo) {
            flatten.push({
              label: screenLabel,
              to: screenInfo.to,
              fullPath: screenInfo.fullPath,
              category: category,
            });
          } else {
            flatten.push({
              label: screenLabel,
              to: `/${screenLabel.toLowerCase().replace(/\s+/g, "")}`,
              fullPath: `${category} > ${screenLabel}`,
              category: category,
            });
          }
        });
      });
    }

    return flatten;
  }, [sideMenuDetails, menuPathMapping, isAdmin]);

  const filteredMenu = useMemo(() => {
    if (!searchText.trim()) return [];

    const query = searchText.toLowerCase();
    return flattenedMenu.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.fullPath.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
    );
  }, [searchText, flattenedMenu]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setShowResults(value.length > 0);
  };

  const clearSearch = () => {
    setSearchText("");
    setShowResults(false);
  };

  const handleResultClick = (to) => {
    navigate(to);
    setSearchText("");
    setShowResults(false);
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (searchText.length > 0) {
      setShowResults(true);
    }
  };

  return (
    <div className="w-full md:mx-auto md:py-[11px] rounded-xl flex md:justify-end">
      <div
        className="relative rounded-xl bg-white w-full md:mb-6"
        ref={searchRef}
      >
        <div
          className={`relative flex items-center transition-all duration-200  rounded-xl
        ${showResults ? "rounded-b-none border-b-0" : ""}`}
        >
          <MagnifyingGlassIcon className="absolute left-2 !h-5 !w-5 !text-gray-900" />
          <input
            type="text"
            placeholder="Find a screen"
            value={searchText}
            onChange={handleSearch}
            onFocus={handleFocus}
            className="flex-1 pl-5 pr-10 !sm:mr-4 rounded-xl py-2 !text-gray-900 !placeholder-gray-900  focus:outline-none focus:ring-0 text-base bg-white"
          />

          <button
            onClick={() => {
              if (searchText.trim()) {
                setShowResults(true);
              }
            }}
            className=" text-black-500  hover:text-black-700 "
          >
            <PaperAirplaneIcon
              className="!h-9 !w-9 !px-2 mr-2 rounded-lg"
              style={{ backgroundColor: "#F2C646" }}
            />
          </button>
        </div>

        {showResults && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg shadow-lg z-50 max-h-96 overflow-y-auto">
            {filteredMenu.length > 0 ? (
              <div className="py-1">
                {filteredMenu.map((item, index) => (
                  <button
                    key={`${item.category}-${item.to}-${index}`}
                    onClick={() => handleResultClick(item.to)}
                    className="w-full text-left px-4 py-2 hover:bg-[var(--primary-background)] transition-colors duration-150 group border-b border-gray-300 last:border-b-0"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {item.label}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 mt-1 truncate">
                          {item.fullPath}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-center">
                <MagnifyingGlassIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No results found for</p>
                <p className="text-gray-700 font-medium">"{searchText}"</p>
                <p className="text-gray-400 text-xs mt-2">
                  Try different keywords
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScreenSearch;
